import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BarChart3, Lock, Loader2, LogOut, RefreshCcw, ShieldCheck } from 'lucide-react';
import SiteFooter from '../components/SiteFooter';
import SiteHeader from '../components/SiteHeader';
import {
  clearAdminSession,
  fetchCassemsAdminMetrics,
  getAdminSession,
  loginCassemsAdmin,
  type CassemsAdminMetrics,
  type CassemsAdminSession,
} from '../lib/cassemsAdmin';
import { formatBRL } from '../data/catalogoDestaque';

type RangePreset = 'today' | '7d' | '30d';

function formatDateTime(value: string): string {
  return new Date(value).toLocaleString('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

function toDateInputValue(date: Date): string {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function buildRange(preset: RangePreset) {
  const end = new Date();
  const start = new Date(end);
  if (preset === 'today') start.setHours(0, 0, 0, 0);
  if (preset === '7d') start.setDate(start.getDate() - 7);
  if (preset === '30d') start.setDate(start.getDate() - 30);
  return {
    from: toDateInputValue(start),
    to: toDateInputValue(end),
  };
}

function statusTone(status: string): string {
  if (status === 'enviado') return 'text-emerald-300 bg-emerald-500/10 border-emerald-500/20';
  if (status === 'parcial') return 'text-amber-300 bg-amber-500/10 border-amber-500/20';
  if (status === 'falha') return 'text-red-300 bg-red-500/10 border-red-500/20';
  return 'text-lunins-ink/80 bg-lunins-sand border-lunins-border';
}

export default function CassemsAdmin() {
  const [pin, setPin] = useState('');
  const [session, setSession] = useState<CassemsAdminSession | null>(null);
  const [metrics, setMetrics] = useState<CassemsAdminMetrics | null>(null);
  const [preset, setPreset] = useState<RangePreset>('7d');
  const [from, setFrom] = useState(() => buildRange('7d').from);
  const [to, setTo] = useState(() => buildRange('7d').to);
  const [loading, setLoading] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'Painel Cassems | Lunins';
    setSession(getAdminSession());
  }, []);

  async function loadMetrics(activeSession: CassemsAdminSession, nextFrom = from, nextTo = to) {
    setLoading(true);
    setError(null);
    try {
      const payload = await fetchCassemsAdminMetrics({
        token: activeSession.token,
        from: `${nextFrom}T00:00:00-04:00`,
        to: `${nextTo}T23:59:59-04:00`,
      });
      setMetrics(payload);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Falha ao carregar painel.';
      if (/sessão expirada|inválida/i.test(message)) {
        clearAdminSession();
        setSession(null);
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (session) void loadMetrics(session);
  }, [session]);

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setAuthLoading(true);
    setError(null);
    try {
      const nextSession = await loginCassemsAdmin(pin);
      setSession(nextSession);
      setPin('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível validar o PIN.');
    } finally {
      setAuthLoading(false);
    }
  }

  function applyPreset(nextPreset: RangePreset) {
    setPreset(nextPreset);
    const nextRange = buildRange(nextPreset);
    setFrom(nextRange.from);
    setTo(nextRange.to);
    if (session) void loadMetrics(session, nextRange.from, nextRange.to);
  }

  const statusResumo = useMemo(() => {
    return metrics?.statusNotificacao ?? [];
  }, [metrics]);

  if (!session) {
    return (
      <div className="flex min-h-screen flex-col">
        <SiteHeader />
        <main className="flex flex-1 items-center justify-center bg-hero-mesh px-4 py-16">
          <div className="w-full max-w-md rounded-3xl border border-lunins-border bg-lunins-surface/70 p-6 shadow-card">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-lunins-accent/15 text-lunins-accent">
              <Lock className="h-7 w-7" />
            </div>
            <h1 className="mt-5 text-center font-display text-2xl font-bold text-lunins-ink">Painel Cassems</h1>
            <p className="mt-3 text-center text-sm leading-relaxed text-lunins-muted">
              Acesso interno para operação e acompanhamento do funil, pedidos e notificações da campanha.
            </p>
            <form className="mt-8 space-y-4" onSubmit={handleLogin}>
              <label className="block">
                <span className="text-sm font-medium text-lunins-ink/80">PIN</span>
                <input
                  type="password"
                  inputMode="numeric"
                  autoComplete="current-password"
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  className="mt-1.5 min-h-[48px] w-full rounded-xl border border-lunins-border bg-lunins-cream/50 px-4 py-3 text-lunins-ink placeholder:text-lunins-muted focus:border-lunins-accent/50 focus:outline-none focus:ring-2 focus:ring-lunins-accent/20"
                  placeholder="Digite o PIN"
                />
              </label>
              {error ? <p className="rounded-lg bg-red-500/10 px-3 py-2 text-sm text-red-300">{error}</p> : null}
              <button
                type="submit"
                disabled={authLoading}
                className="flex min-h-[52px] w-full items-center justify-center gap-2 rounded-full bg-lunins-ink py-4 text-base font-semibold text-lunins-cream transition hover:bg-lunins-ink-soft disabled:opacity-60"
              >
                {authLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ShieldCheck className="h-5 w-5" />}
                {authLoading ? 'Validando acesso…' : 'Entrar no painel'}
              </button>
            </form>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="flex-1 bg-hero-mesh px-4 py-8 md:py-10">
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-col gap-4 rounded-3xl border border-lunins-border bg-lunins-surface/50 p-5 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-lunins-accent">Painel interno</p>
              <h1 className="mt-2 font-display text-2xl font-bold text-lunins-ink md:text-3xl">Funil e encomendas Cassems</h1>
              <p className="mt-2 text-sm text-lunins-muted">
                Leitura rápida do comportamento da campanha, pedidos registrados e status das automações.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Link
                to="/cassems"
                className="inline-flex min-h-[44px] items-center justify-center rounded-full border border-lunins-border px-4 py-2 text-sm font-medium text-lunins-ink/80 hover:bg-lunins-sand"
              >
                Ver landing
              </Link>
              <button
                type="button"
                onClick={() => session && loadMetrics(session)}
                className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full border border-lunins-border px-4 py-2 text-sm font-medium text-lunins-ink hover:bg-lunins-sand"
              >
                <RefreshCcw className="h-4 w-4" />
                Atualizar
              </button>
              <button
                type="button"
                onClick={() => {
                  clearAdminSession();
                  setSession(null);
                  setMetrics(null);
                }}
                className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full bg-lunins-sand px-4 py-2 text-sm font-medium text-lunins-ink/80 hover:bg-lunins-sand"
              >
                <LogOut className="h-4 w-4" />
                Sair
              </button>
            </div>
          </div>

          <div className="mt-6 flex flex-col gap-3 rounded-2xl border border-lunins-border bg-lunins-surface/40 p-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => applyPreset('today')}
                className={`rounded-full px-4 py-2 text-sm font-semibold ${preset === 'today' ? 'bg-lunins-accent text-lunins-ink' : 'bg-lunins-sand text-lunins-ink/80'}`}
              >
                Hoje
              </button>
              <button
                type="button"
                onClick={() => applyPreset('7d')}
                className={`rounded-full px-4 py-2 text-sm font-semibold ${preset === '7d' ? 'bg-lunins-accent text-lunins-ink' : 'bg-lunins-sand text-lunins-ink/80'}`}
              >
                7 dias
              </button>
              <button
                type="button"
                onClick={() => applyPreset('30d')}
                className={`rounded-full px-4 py-2 text-sm font-semibold ${preset === '30d' ? 'bg-lunins-accent text-lunins-ink' : 'bg-lunins-sand text-lunins-ink/80'}`}
              >
                30 dias
              </button>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <label className="text-sm text-lunins-ink/80">
                <span className="mb-1 block">De</span>
                <input
                  type="date"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                  className="min-h-[44px] rounded-xl border border-lunins-border bg-lunins-cream/50 px-3 text-lunins-ink"
                />
              </label>
              <label className="text-sm text-lunins-ink/80">
                <span className="mb-1 block">Até</span>
                <input
                  type="date"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  className="min-h-[44px] rounded-xl border border-lunins-border bg-lunins-cream/50 px-3 text-lunins-ink"
                />
              </label>
              <button
                type="button"
                onClick={() => session && loadMetrics(session)}
                className="inline-flex min-h-[44px] items-center justify-center rounded-full bg-lunins-ink px-5 py-2 text-sm font-semibold text-lunins-cream"
              >
                Aplicar período
              </button>
            </div>
          </div>

          {error ? <p className="mt-4 rounded-lg bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</p> : null}

          {loading && !metrics ? (
            <div className="mt-10 flex items-center justify-center gap-2 text-lunins-ink/80">
              <Loader2 className="h-5 w-5 animate-spin" />
              Carregando painel…
            </div>
          ) : null}

          {metrics ? (
            <>
              <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                {[
                  { label: 'Visitantes landing', value: metrics.summary.visitantesLanding },
                  { label: 'Sessões com carrinho', value: metrics.summary.sessoesAddToCart },
                  { label: 'Checkouts', value: metrics.summary.sessoesCheckout },
                  { label: 'Pedidos registrados', value: metrics.summary.pedidosRegistrados },
                  { label: 'Receita total', value: formatBRL(metrics.summary.receitaTotal) },
                  { label: 'Ticket médio', value: formatBRL(metrics.summary.ticketMedio) },
                  { label: 'Itens vendidos', value: metrics.summary.itensVendidos },
                  { label: 'Conversão landing → pedido', value: `${metrics.summary.taxaConversao.toFixed(1)}%` },
                ].map((card) => (
                  <div key={card.label} className="rounded-2xl border border-lunins-border bg-lunins-surface/50 p-5">
                    <p className="text-xs font-semibold uppercase tracking-wide text-lunins-muted">{card.label}</p>
                    <p className="mt-3 font-display text-2xl font-bold text-lunins-ink">{card.value}</p>
                  </div>
                ))}
              </div>

              <div className="mt-6 grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
                <section className="rounded-3xl border border-lunins-border bg-lunins-surface/45 p-5">
                  <div className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-lunins-accent" />
                    <h2 className="font-display text-xl font-bold text-lunins-ink">Funil da campanha</h2>
                  </div>
                  <div className="mt-6 grid gap-3 md:grid-cols-4">
                    {[
                      { label: 'Landing', value: metrics.summary.visitantesLanding },
                      { label: 'Carrinho', value: metrics.summary.sessoesAddToCart },
                      { label: 'Checkout', value: metrics.summary.sessoesCheckout },
                      { label: 'Pedido', value: metrics.summary.pedidosRegistrados },
                    ].map((step) => (
                      <div key={step.label} className="rounded-2xl border border-lunins-border bg-lunins-sand/60 p-4">
                        <p className="text-xs uppercase tracking-wide text-lunins-muted">{step.label}</p>
                        <p className="mt-3 font-display text-3xl font-bold text-lunins-accent">{step.value}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 overflow-hidden rounded-2xl border border-lunins-border">
                    <table className="min-w-full text-sm">
                      <thead className="bg-lunins-sand text-lunins-muted">
                        <tr>
                          <th className="px-4 py-3 text-left">Dia</th>
                          <th className="px-4 py-3 text-right">Visitantes</th>
                          <th className="px-4 py-3 text-right">Carrinho</th>
                          <th className="px-4 py-3 text-right">Checkout</th>
                          <th className="px-4 py-3 text-right">Pedidos</th>
                          <th className="px-4 py-3 text-right">Receita</th>
                        </tr>
                      </thead>
                      <tbody>
                        {metrics.dailySeries.slice(-10).reverse().map((row) => (
                          <tr key={row.dia} className="border-t border-lunins-border text-lunins-ink/80">
                            <td className="px-4 py-3">{row.dia}</td>
                            <td className="px-4 py-3 text-right">{row.visitantes}</td>
                            <td className="px-4 py-3 text-right">{row.addToCart}</td>
                            <td className="px-4 py-3 text-right">{row.checkout}</td>
                            <td className="px-4 py-3 text-right">{row.pedidosRegistrados}</td>
                            <td className="px-4 py-3 text-right">{formatBRL(row.receita)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>

                <section className="space-y-6">
                  <div className="rounded-3xl border border-lunins-border bg-lunins-surface/45 p-5">
                    <h2 className="font-display text-xl font-bold text-lunins-ink">Status das notificações</h2>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {statusResumo.map((row) => (
                        <div key={row.status} className={`rounded-full border px-3 py-2 text-sm font-semibold ${statusTone(row.status)}`}>
                          {row.status}: {row.total}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-3xl border border-lunins-border bg-lunins-surface/45 p-5">
                    <h2 className="font-display text-xl font-bold text-lunins-ink">Top produtos</h2>
                    <div className="mt-4 space-y-3">
                      {metrics.topProdutos.map((item) => (
                        <div key={item.produto} className="rounded-2xl border border-lunins-border bg-lunins-cream/35 p-4">
                          <div className="flex items-center justify-between gap-4">
                            <p className="font-medium text-lunins-ink">{item.produto}</p>
                            <p className="text-sm text-lunins-accent">{formatBRL(item.receita)}</p>
                          </div>
                          <p className="mt-2 text-xs text-lunins-muted">{item.quantidade} unidade(s) vendidas</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-3xl border border-lunins-border bg-lunins-surface/45 p-5">
                    <h2 className="font-display text-xl font-bold text-lunins-ink">Origens</h2>
                    <div className="mt-4 space-y-3">
                      {metrics.origens.map((source) => (
                        <div key={source.origem} className="rounded-2xl border border-lunins-border bg-lunins-cream/35 p-4">
                          <p className="text-sm font-medium text-lunins-ink">{source.origem}</p>
                          <p className="mt-2 text-xs text-lunins-muted">
                            {source.visitas} visita(s) · {source.pedidos} pedido(s) trackeado(s)
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </section>
              </div>

              <section className="mt-6 rounded-3xl border border-lunins-border bg-lunins-surface/45 p-5">
                <h2 className="font-display text-xl font-bold text-lunins-ink">Encomendas registradas</h2>
                <div className="mt-5 overflow-auto">
                  <table className="min-w-full text-sm">
                    <thead className="text-lunins-muted">
                      <tr>
                        <th className="px-3 py-3 text-left">Data</th>
                        <th className="px-3 py-3 text-left">Cliente</th>
                        <th className="px-3 py-3 text-left">Setor</th>
                        <th className="px-3 py-3 text-left">Produtos</th>
                        <th className="px-3 py-3 text-right">Total</th>
                        <th className="px-3 py-3 text-left">Automação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {metrics.recentOrders.map((order) => (
                        <tr key={order.id} className="border-t border-lunins-border text-lunins-ink/80 align-top">
                          <td className="px-3 py-4 whitespace-nowrap">{formatDateTime(order.createdAt)}</td>
                          <td className="px-3 py-4 min-w-[12rem]">
                            <p className="font-medium text-lunins-ink">{order.cliente}</p>
                            {order.telefone ? <p className="mt-1 text-xs text-lunins-muted">{order.telefone}</p> : null}
                          </td>
                          <td className="px-3 py-4">{order.setor || '—'}</td>
                          <td className="px-3 py-4 min-w-[18rem] text-xs text-lunins-muted">{order.itensResumo || '—'}</td>
                          <td className="px-3 py-4 text-right font-semibold text-lunins-ink">{formatBRL(order.total)}</td>
                          <td className="px-3 py-4 min-w-[12rem]">
                            <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${statusTone(order.notificacaoStatus)}`}>
                              {order.notificacaoStatus}
                            </span>
                            {order.notificacaoErro ? <p className="mt-2 text-xs text-amber-300/80">{order.notificacaoErro}</p> : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            </>
          ) : null}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
